/**
 * A helper service that validates the SSO settings the app received from the implementation.
 * @ngesdoc service
 * @ngname ssoValidations
 * @ngmodule auth
 */
class SSOValidations {
  /**
   * @param {Window}    $window   To check if an `opener` exists on a popup implementation.
   * @param {SSOClient} ssoClient To validate authentication reasons.
   */
  constructor($window, ssoClient) {
    'ngInject';

    /**
     * A local reference for the browser window.
     * @type {Window}
     */
    this.$window = $window;
    /**
     * A local reference for the `ssoClient` service.
     * @type {SSOClient}
     */
    this.ssoClient = ssoClient;
    /**
     * A dictionary with the messages for the possible authentication reasons. They'll be used
     * on the `validateReason` method.
     * @type {Object}
     * @access protected
     * @ignore
     */
    this._authenticationReasons = this._generateAuthenticationReasons();
  }
  /**
   * Checks if a callback URL domain matches the one the where the cookie will be written.
   * @param {string} url The URL to validate.
   * @return {boolean}
   */
  validateCallback(url) {
    return this.ssoClient.isCallbackURLValid(url);
  }
  /**
   * Checks if a the current window has an `opener` property. This is for `popup` implementations,
   * where the app needs the `opener` in order to send the post message informing the result of
   * the authentication process.
   * @return {boolean}
   */
  validatePopup() {
    return !!this.$window.opener;
  }
  /**
   * Validates an authentication reason recevied on as a parameter. Instead of returning a
   * `boolean` value, this method will try to get the _"human readable message"_ for the reason
   * and return it in case the reason is _"valid"_.
   * @param {string} reason The recevied reason identifier. This should be a constant obtained from
   *                        the SSO Client.
   * @return {?string} If the reaso is _"valid"_, it will return a _"human readable message"_,
   *                   but if isn't, it will return `null`.
   */
  validateReason(reason) {
    return this._authenticationReasons[reason] || null;
  }
  /**
   * Generates the dictionary with the messages for the supported authentication reasons.
   * @return {Object}
   * @access protected
   * @ignore
   */
  _generateAuthenticationReasons() {
    const reasons = this.ssoClient.getAuthenticationReasons();
    return {
      [reasons.expiration]: 'Your session has expired',
      [reasons.release]: 'A new version has been released',
    };
  }
}

export default SSOValidations;
